namespace $ {

	export class $shm_hitalama_map_widget_entity_table2_relation extends $giper_baza_entity.with({
		Values: $giper_baza_list_link,
		Pages: $giper_baza_dict_to( $giper_baza_atom_link ),
	}) {}

	export class $shm_hitalama_map_widget_entity_table2_dataset extends $shm_hitalama_map_widget_entity_form_custom.with({
		Map: $giper_baza_atom_link.to( ()=> $shm_hitalama_map ),
		Fields: $giper_baza_list_link.to( ()=> $shm_hitalama_map_widget_entity_table2_field ),
		Records: $giper_baza_list_link.to( ()=> $shm_hitalama_map_widget_entity_table2_record ),
		Record_pages: $giper_baza_dict_to( $giper_baza_atom_link ),
		Record_shards: $giper_baza_dict_to( $giper_baza_atom_link ),
		Record_count: $giper_baza_atom_real,
		Record_sequence: $giper_baza_atom_real,
		Draft: $giper_baza_atom_link.to( ()=> $shm_hitalama_map_widget_entity_table2_record ),
		Storage_version: $giper_baza_atom_real,
	}) {

		static readonly system_names = [ 'Дата создания', 'Дата обновления', 'Создал', 'Изменил' ] as const
		// The map renders only a small first page. A 256-record land made that read
		// download and verify hundreds of unrelated records on the main thread.
		// Existing record links remain valid; only new allocations use this size.
		static readonly record_shard_size = 16
		static readonly record_page_size = 256
		static readonly relation_page_size = 256
		static readonly storage_version = 5

		private record_page_lands = new Map< number, $shm_hitalama_map_widget_entity_table2_dataset_page_land >()

		destructor = ()=> {
			for( const keeper of this.record_page_lands.values() ) keeper.destructor()
			this.record_page_lands.clear()
			super.destructor()
		}

		map() {
			return this.Map()?.remote() ?? null
		}

		map_bind( map: $shm_hitalama_map ) {
			if( this.Map()?.remote()?.link().str !== map.link().str ) this.Map( null )?.remote( map )
			return map
		}

		history_events() {
			return this.map()?.history_maybe()?.events_for( this ) ?? []
		}

		history_scope_targets( records: readonly $shm_hitalama_map_widget_entity_table2_record[] ) {
			return [ ... new Set( records.flatMap( record => record.history_scope_targets() ) ) ]
		}

		record_snapshot( record: $shm_hitalama_map_widget_entity_table2_record ) {
			return this.fields().map( field => ({
				field: field.link().str,
				value: $shm_hitalama_map_history.value( record.value( field ) ),
			}) )
		}

		@ $mol_action
		initialize() {
			// Create the structural pawns before a form can subscribe to an empty dataset.
			// Otherwise its first read has no Fields/Records publisher and stays empty
			// until the whole map is opened again.
			this.Fields( null )
			this.Records( null )
			this.Record_pages( null )
			this.Record_shards( null )
			this.Draft( null )
			return this
		}

		@ $mol_mem
		fields() {
			return this.Fields()?.remote_list() ?? []
		}

		@ $mol_mem_key
		query_snapshot( land: $giper_baza_land ) {
			return land.Pawn( this.$.$shm_hitalama_map_widget_entity_table2_querysnapshot ).Head(
				this.$.$shm_hitalama_map_widget_entity_table2_querysnapshot.head_for( this ),
			)
		}

		query_snapshot_row( record: $shm_hitalama_map_widget_entity_table2_record ) {
			return this.query_snapshot( record.land() ).rows( this.fields() )?.get( record.link().str ) ?? null
		}

		query_snapshot_values( record: $shm_hitalama_map_widget_entity_table2_record, columns: readonly number[] ) {
			return this.query_snapshot( record.land() ).column_rows([ this.fields(), columns ])?.get( record.link().str ) ?? null
		}

		fields_reorder_current() {
			return this.fields()
		}

		@ $mol_action
		fields_reorder( fields: readonly $shm_hitalama_map_widget_entity_table2_field[] ) {
			const current = $mol_wire_sync( this ).fields_reorder_current()
			if( fields.length !== current.length ) return false
			const current_links = new Set( current.map( field => field.link().str ) )
			if( new Set( fields.map( field => field.link().str ) ).size !== fields.length ) return false
			if( fields.some( field => !current_links.has( field.link().str ) ) ) return false
			if( fields.every( ( field, index )=> field.link().str === current[ index ].link().str ) ) return true
			return $shm_hitalama_map_history.transaction( this.map(), 'Изменение порядка свойств', ()=> {
				this.Fields( null )!.remote_list( fields )
				$shm_hitalama_map_history.capture( this.map(), {
					type: 'dataset_schema', target: this.link().str,
					before: { fields: current.map( field => field.link().str ) },
					after: { fields: fields.map( field => field.link().str ) },
				}, 'Изменение порядка свойств' )
				return true
			} )
		}

		field( name: string ) {
			return this.fields().find( field => field.title() === name ) ?? null
		}

		@ $mol_mem
		record_refs_all() {
			const pages = this.record_page_entries()
			return [ ... new Map(
				pages.flatMap( page => this.record_page_links( page.index ) ).map( link => {
					const record = this.$.$giper_baza_glob.Pawn(
						link,
						this.$.$shm_hitalama_map_widget_entity_table2_record,
					)
					return [ record.link().str, record ] as const
				} ),
			).values() ]
		}

		private readonly record_page_snapshots = new Map< number, {
			key: string
			land: $giper_baza_land
			auth: $giper_baza_auth
			access: readonly ( $giper_baza_auth_pass | $giper_baza_unit_gift )[]
			units: readonly $giper_baza_unit_sand[]
			links: readonly $giper_baza_link[]
		} >()

		@ $mol_mem_key
		record_page_links( index: number ) {
			if( !Number.isSafeInteger( index ) || index < 0 ) return [] as readonly $giper_baza_link[]
			const page = this.record_page( index )
			if( !page ) return [] as readonly $giper_baza_link[]
			page.land.sync()
			// Unobserved wire channels can be collected between append commands.
			// Compare the ordered structure before units() opens payloads. Keep the
			// same peer/alive selection as Pawn.units(). Subscribe to raw permission
			// units without introducing first-time key decryption into a local write.
			// Changed permissions are checked before reusing decoded values.
			const land = page.list.land()
			const auth = land.auth()
			const access = [ ...land._gift.values(), ...land._pass.values() ]
			const inherited = Boolean( land.Tine()?.items_vary().length )
			const units = land.sand_ordered({ head: page.list.head(), peer: $giper_baza_link.hole })
				.filter( unit=> !unit.dead() && unit.self().str !== '' )
			const key = page.list.link().str
			const cached = this.record_page_snapshots.get( index )
			const same_access = cached?.land === land && cached.auth === auth && cached.access.length === access.length
				&& access.every( ( unit, index )=> unit === cached.access[ index ] )
			if( cached && !same_access ) land.secret()
			if( !inherited && cached?.key === key && same_access && cached.units.length === units.length
				&& units.every( ( unit, index )=> unit === cached.units[ index ] ) ) return cached.links
			const links = page.list.items()
			this.record_page_snapshots.set( index, { key, land, auth, access, units: [ ...units ], links } )
			return links
		}

		@ $mol_mem
		records_all() {
			const records = this.record_refs_all()
			const lands = new Map( records.map( record => [ record.land().link().str, record.land() ] ) )
			for( const land of lands.values() ) land.sync()
			return records
		}

		private area_grab() {
			const source = this.land()
			const access = this.map()?.access_level()
			const public_rank = access === 'public_edit'
				? this.$.$giper_baza_rank_post( 'just' )
				: source.lord_rank( null )
			const preset: $giper_baza_rank_preset = [[ null, public_rank ]]
			const gifts: Iterable< $giper_baza_unit_gift > = ( source as any )._gift?.values?.() ?? []
			for( const gift of gifts ) {
				const mate = gift.mate()
				if( !mate.str ) continue
				const pass = source.lord_pass( mate )
				if( pass ) preset.push([ pass, gift.rank() ])
			}
			const owner = source.king_pass()
			if( owner.lord().str !== this.$.$giper_baza_auth.current().pass().lord().str ) {
				preset.push([ owner, this.$.$giper_baza_rank_rule ])
			}
			return this.$.$shm_hitalama_map_widget_entity_table2_allocation.land_grab( preset, this.$ )
		}

		private indexed_area(
			index: { key( key: $giper_baza_vary_type, auto?: any ): $giper_baza_atom_link | null },
			key: string,
		) {
			const current = index.key( key )?.val()
			// A reference alone does not load the shard's grants. In particular,
			// self_make() checks rights without synchronizing the Land first.
			if( current ) return this.$.$giper_baza_glob.Land( current ).sync()
			const land = this.area_grab()
			const latest = index.key( key )?.val()
			if( latest ) return this.$.$giper_baza_glob.Land( latest ).sync()
			index.key( key, 'auto' )!.val( land.link() )
			return land
		}

		@ $mol_mem_key
		record_shard( index: number ) {
			return this.indexed_area( this.Record_shards( null )!, String( index ) )
		}

		record_page_entries() {
			const root = this.Records()
			if( !root ) return [] as Array<{ index: number, land: $giper_baza_land, list: $giper_baza_list_link }>
			const result: Array<{ index: number, land: $giper_baza_land, list: $giper_baza_list_link }> = [
				{ index: 0, land: root.land(), list: root },
			]
			const pages = this.Record_pages()
			if( !pages ) return result
			for( const key of pages.keys() ) {
				const index = Number( key )
				const link = pages.key( key )?.val()
				if( !Number.isSafeInteger( index ) || index <= 0 || !link ) continue
				const page = this.record_page( index )
				if( page ) result.push( page )
			}
			return result.sort( ( left, right )=> left.index - right.index )
		}

		record_count() {
			if( this.record_index_active() ) return this.Record_count()?.val() ?? 0
			const pages = this.record_page_entries()
			if( !pages.length ) return 0
			if( pages.length === 1 ) return pages[ 0 ].list.items().length
			const last = pages[ pages.length - 1 ]
			return last.index * $shm_hitalama_map_widget_entity_table2_dataset.record_page_size + last.list.items().length
		}

		record_sequence() {
			if( this.record_index_active() ) return this.Record_sequence()?.val() ?? this.record_count()
			const pages = this.record_page_entries()
			if( !pages.length ) return 0
			const last = pages[ pages.length - 1 ]
			return last.index * $shm_hitalama_map_widget_entity_table2_dataset.record_page_size + last.list.items().length
		}

		record_index_active() {
			return this.storage_version() >= $shm_hitalama_map_widget_entity_table2_dataset.storage_version
				&& Boolean( this.Record_count() )
		}

		record_page( index: number ) {
			if( index === 0 ) {
				const list = this.Records()
				return list ? { index, land: list.land(), list } : null
			}
			const link = this.Record_pages()?.key( String( index ) )?.val()
			let keeper = this.record_page_lands.get( index )
			if( keeper && keeper.link().str !== link?.str ) {
				keeper.destructor()
				this.record_page_lands.delete( index )
				keeper = undefined
			}
			if( !link ) return null
			if( !keeper ) {
				keeper = $shm_hitalama_map_widget_entity_table2_dataset_page_land.make({
					$: this.$,
					link: $mol_const( link ),
				})
				this.record_page_lands.set( index, keeper )
			}
			const land = keeper.read()
			return { index, land, list: land.Data( $giper_baza_list_link ) }
		}

		record_link_at( index: number ) {
			if( !Number.isSafeInteger( index ) || index < 0 || index >= this.record_count() ) return ''
			const size = $shm_hitalama_map_widget_entity_table2_dataset.record_page_size
			const page_index = Math.floor( index / size )
			const page = this.record_page( page_index )
			if( !page ) return ''
			page.land.sync()
			return page.list.items()[ index % size ]?.str ?? ''
		}

		record_at( index: number ) {
			const link = this.record_link_at( index )
			return link ? this.record_by_link_direct( link ) : null
		}

		@ $mol_action
		record_index_migrate() {
			const root = this.Records( null )!
			const links = root.items()
			const size = $shm_hitalama_map_widget_entity_table2_dataset.record_page_size
			if( links.length <= size ) return false
			const pages = this.Record_pages( null )!
			for( let offset = size; offset < links.length; offset += size ) {
				const index = Math.floor( offset / size )
				this.indexed_area( pages, String( index ) ).Data( $giper_baza_list_link )
					.items( links.slice( offset, offset + size ) )
			}
			root.items( links.slice( 0, size ) )
			return true
		}

		@ $mol_action
		record_index_append( records: readonly $shm_hitalama_map_widget_entity_table2_record[] ) {
			if( !this.record_index_active() ) this.record_index_activate()
			this.record_index_migrate()
			let append = [ ... new Map( records.map( record => [ record.link().str, record ] as const ) ).values() ]
			if( !append.length ) return records
			const size = $shm_hitalama_map_widget_entity_table2_dataset.record_page_size
			const indexed = new Set( this.record_index_links() )
			append = append.filter( record => !indexed.has( record.link().str ) )
			if( !append.length ) return records
			let count = this.record_count()
			// Resolve every page before publishing any links. Allocating/loading a
			// later page can suspend; otherwise a restart sees the already written
			// links but the old count and can overfill the previous page.
			const lists = new Map< number, $giper_baza_list_link >()
			const first = Math.floor( count / size )
			const last = Math.floor( ( count + append.length - 1 ) / size )
			for( let page = first; page <= last; ++page ) {
				const list = page === 0 ? this.Records( null )!
					: this.indexed_area( this.Record_pages( null )!, String( page ) ).Data( $giper_baza_list_link )
				list.items()
				lists.set( page, list )
			}
			while( append.length ) {
				const page = Math.floor( count / size )
				const list = lists.get( page )!
				const current = list.items()
				const chunk = append.splice( 0, Math.max( 1, size - current.length ) )
				list.splice( chunk.map( record => record.link() ) )
				count += chunk.length
			}
			this.Record_count( null )!.val( count )
			return records
		}

		record_index_links() {
			const pages = this.record_page_entries()
			return pages.flatMap( page => this.record_page_links( page.index ).map( link => link.str ) )
		}

		private record_index_write( links: readonly string[] ) {
			const size = $shm_hitalama_map_widget_entity_table2_dataset.record_page_size
			const pages = this.record_page_entries()
			const needed = Math.max( 1, Math.ceil( links.length / size ) )
			for( let index = 0; index < Math.max( needed, pages.length ); ++ index ) {
				const list = index === 0 ? this.Records( null )!
					: pages.find( page => page.index === index )?.list
						?? this.indexed_area( this.Record_pages( null )!, String( index ) ).Data( $giper_baza_list_link )
				list.items( links.slice( index * size, ( index + 1 ) * size ).map( link => new $giper_baza_link( link ) ) )
			}
			this.Record_count( null )!.val( links.length )
			return links
		}

		@ $mol_action
		record_index_activate() {
			if( this.record_index_active() ) return false
			this.record_index_migrate()
			const sequence = this.record_sequence()
			const records = this.records_all()
			const active = records.filter( record => this.$.$mol_error_fence(
				() => !record.deleted(),
				error => error instanceof RangeError && error.message === 'Invalid array length' ? false : error,
			) )
			this.record_index_write( active.map( record => record.link().str ) )
			this.Record_sequence( null )!.val( sequence )
			this.Storage_version( null )!.val( $shm_hitalama_map_widget_entity_table2_dataset.storage_version )
			return true
		}

		@ $mol_action
		record_index_remove( records: readonly $shm_hitalama_map_widget_entity_table2_record[] ) {
			if( !this.record_index_active() ) this.record_index_activate()
			const remove = new Set( records.map( record => record.link().str ) )
			this.record_index_write( this.record_index_links().filter( link => !remove.has( link ) ) )
			return records
		}

		@ $mol_action
		record_restore_links( records: readonly $shm_hitalama_map_widget_entity_table2_record[] ) {
			this.record_index_append( records )
			return records
		}

		relation_root(
			record: $shm_hitalama_map_widget_entity_table2_record,
			field: $shm_hitalama_map_widget_entity_table2_field,
			auto?: null,
		) {
			const roots = record.Relation_roots( auto )
			if( !roots ) return null
			const key = record.field_key( field )
			const current = roots.key( key )?.val()
			if( current ) {
				const land = this.$.$giper_baza_glob.Land( current )
				if( auto === undefined ) land.sync()
				return land.Data( $shm_hitalama_map_widget_entity_table2_relation )
			}
			if( auto === undefined ) return null
			const land = this.indexed_area( roots, key )
			const relation = land.Data( $shm_hitalama_map_widget_entity_table2_relation )
			relation.Values( null )
			relation.Pages( null )
			return relation
		}

		relation_page_refs( record: $shm_hitalama_map_widget_entity_table2_record, field: $shm_hitalama_map_widget_entity_table2_field ) {
			const relation = this.relation_root( record, field )
			if( !relation ) return [] as Array<{ index: number, land: $giper_baza_land, list: $giper_baza_list_link }>
			// A remote root link may arrive before the referenced land. Reading must stay
			// side-effect free and wait for reactive synchronization instead of trying to
			// materialize Values in a placeholder land with insufficient rank.
			const values = relation.Values()
			if( !values ) return [] as Array<{ index: number, land: $giper_baza_land, list: $giper_baza_list_link }>
			const result = [{ index: 0, land: relation.land(), list: values }]
			const pages = relation.Pages()
			if( !pages ) return result
			return [ ... result, ... pages.keys().flatMap( key => {
				const page = Number( key )
				const link = pages.key( key )?.val()
				if( !Number.isSafeInteger( page ) || page <= 0 || !link ) return []
				const land = this.$.$giper_baza_glob.Land( link )
				return [{ index: page, land, list: land.Data( $giper_baza_list_link ) }]
			} ) ].sort( ( left, right )=> left.index - right.index )
		}

		relation_page_entries( record: $shm_hitalama_map_widget_entity_table2_record, field: $shm_hitalama_map_widget_entity_table2_field ) {
			const pages = this.relation_page_refs( record, field )
			for( const page of pages ) page.land.sync()
			return pages
		}

		relation_has_pages( record: $shm_hitalama_map_widget_entity_table2_record, field: $shm_hitalama_map_widget_entity_table2_field ) {
			return Boolean( this.relation_root( record, field ) )
		}

		relation_values( record: $shm_hitalama_map_widget_entity_table2_record, field: $shm_hitalama_map_widget_entity_table2_field ) {
			return [ ... new Set( this.relation_page_entries( record, field ).flatMap( page =>
				page.list.items().map( link => link.str )
			) ) ]
		}

		relation_count( record: $shm_hitalama_map_widget_entity_table2_record, field: $shm_hitalama_map_widget_entity_table2_field ) {
			const pages = this.relation_page_refs( record, field )
			if( !pages.length ) return 0
			const last = pages[ pages.length - 1 ]
			last.land.sync()
			return last.index * $shm_hitalama_map_widget_entity_table2_dataset.relation_page_size + last.list.items().length
		}

		relation_slice(
			record: $shm_hitalama_map_widget_entity_table2_record,
			field: $shm_hitalama_map_widget_entity_table2_field,
			offset: number,
			limit: number,
		) {
			if( limit <= 0 ) return []
			const result: string[] = []
			const size = $shm_hitalama_map_widget_entity_table2_dataset.relation_page_size
			const end = offset + limit
			for( const page of this.relation_page_refs( record, field ) ) {
				const start = page.index * size
				if( start >= end || start + size <= offset ) continue
				page.land.sync()
				const values = page.list.items()
				for( let index = Math.max( 0, offset - start ); index < values.length && result.length < limit; ++ index ) {
					result.push( values[ index ].str )
				}
				if( result.length === limit ) break
			}
			return [ ... new Set( result ) ]
		}

		@ $mol_action
		relation_replace(
			record: $shm_hitalama_map_widget_entity_table2_record,
			field: $shm_hitalama_map_widget_entity_table2_field,
			values: readonly string[],
		) {
			const normalized = [ ... new Set( values.map( value => new $giper_baza_link( value ).str ) ) ]
			let relation = this.relation_root( record, field )
			let pages = relation ? this.relation_page_entries( record, field ) : []
			if( !relation ) {
				relation = this.relation_root( record, field, null )!
				pages = [{ index: 0, land: relation.land(), list: relation.Values( null )! }]
			}
			const count = Math.max( 1, Math.ceil( normalized.length / $shm_hitalama_map_widget_entity_table2_dataset.relation_page_size ) )
			for( let page = 0; page < count; ++ page ) {
				const chunk = normalized.slice(
					page * $shm_hitalama_map_widget_entity_table2_dataset.relation_page_size,
					( page + 1 ) * $shm_hitalama_map_widget_entity_table2_dataset.relation_page_size,
				)
				let list = pages.find( item => item.index === page )?.list
				if( !list ) {
					const land = this.indexed_area( relation.Pages( null )!, String( page ) )
					list = land.Data( $giper_baza_list_link )
				}
				list.items( chunk.map( link => new $giper_baza_link( link ) ) )
			}
			for( const page of pages ) if( page.index >= count ) {
				page.list.items([])
				relation.Pages( null )!.has( String( page.index ), false )
			}
			return normalized
		}

		@ $mol_action
		relation_append(
			record: $shm_hitalama_map_widget_entity_table2_record,
			field: $shm_hitalama_map_widget_entity_table2_field,
			values: readonly string[],
		) {
			let append = [ ... new Set( values.map( value => new $giper_baza_link( value ).str ) ) ]
			if( !append.length ) return []
			const pages = this.relation_page_refs( record, field )
			// A wire action may restart after a page area is created. The already written
			// part of the same append is necessarily in the tail, so a bounded tail check
			// makes the operation idempotent without scanning a huge relation each time.
			const tail_count = Math.ceil( append.length / $shm_hitalama_map_widget_entity_table2_dataset.relation_page_size ) + 1
			const tail_pages = pages.slice( -tail_count )
			for( const page of tail_pages ) page.land.sync()
			const tail = new Set( tail_pages.flatMap( page => page.list.items().map( link => link.str ) ) )
			append = append.filter( link => !tail.has( link ) )
			if( !append.length ) return values
			let page = pages.at( -1 )?.index ?? 0
			let land = pages.at( -1 )?.land
			let list = pages.at( -1 )?.list ?? null
			let current = list?.items().map( link => link.str ) ?? []
			const relation = this.relation_root( record, field, null )!
			while( append.length ) {
				if( !list || current.length >= $shm_hitalama_map_widget_entity_table2_dataset.relation_page_size ) {
					if( list ) ++ page
					if( page === 0 ) {
						land = relation.land()
						list = relation.Values( null )!
					} else {
						land = this.indexed_area( relation.Pages( null )!, String( page ) )
						list = land.Data( $giper_baza_list_link )
					}
					current = list.items().map( link => link.str )
				}
				const room = $shm_hitalama_map_widget_entity_table2_dataset.relation_page_size - current.length
				const chunk = append.splice( 0, Math.max( 1, room ) )
				list.splice( chunk.map( link => new $giper_baza_link( link ) ) )
				current = [ ... current, ... chunk ]
			}
			return values
		}

		@ $mol_action
		relation_remove(
			record: $shm_hitalama_map_widget_entity_table2_record,
			field: $shm_hitalama_map_widget_entity_table2_field,
			values: readonly string[],
		) {
			const remove = new Set( values.map( value => new $giper_baza_link( value ).str ) )
			this.relation_replace( record, field, this.relation_values( record, field ).filter( link => !remove.has( link ) ) )
		}

		storage_version() {
			return this.Storage_version()?.val() ?? 1
		}

		@ $mol_action
		records_migrate_initial() {
			this.record_index_activate()
			const fields = this.fields()
			let migrated = 0
			for( const record of this.records_all() ) {
				const initial_values = record.Initial()?.val()
				if( initial_values ) {
					if( initial_values['__packed'] === true || record.Override_keys()?.has( '__packed' ) ) continue
					for( const field of fields ) {
						const overridden = field.type() === 'list_link'
							? this.relation_has_pages( record, field ) || Boolean( record.field_pawn( field ) )
							: Boolean( record.field_override( field )?.val() )
						if( overridden ) record.override_mark( field )
					}
					record.Override_keys( null )!.add( '__packed' )
					++ migrated
					continue
				}
				const system = record.system_values()
				const initial: Record< string, any > = { '__dataset': this.link().str }
				const large_relations: Array<{ field: $shm_hitalama_map_widget_entity_table2_field, links: string[] }> = []
				if( system['Дата создания'] ) initial['__created_at'] = system['Дата создания']
				if( system['Дата обновления'] ) initial['__updated_at'] = system['Дата обновления']
				if( system['Создал'] ) initial['__created_by'] = system['Создал']
				if( system['Изменил'] ) initial['__updated_by'] = system['Изменил']
				for( const field of fields ) {
					const value = record.value( field )
					if( value === '' || value === null || value === undefined ) continue
					if( Array.isArray( value ) && !value.length ) continue
					if(
						field.type() === 'list_link' && Array.isArray( value )
						&& value.length > $shm_hitalama_map_widget_entity_table2_dataset.relation_page_size
					) large_relations.push({ field, links: value.map( String ) })
					else initial[ record.field_key( field ) ] = value
				}
				record.Initial( null )!.val( initial )
				record.Override_keys( null )!.add( '__packed' )
				for( const relation of large_relations ) this.relation_replace( record, relation.field, relation.links )
				++ migrated
			}
			this.Storage_version( null )!.val( $shm_hitalama_map_widget_entity_table2_dataset.storage_version )
			return migrated
		}

		@ $mol_mem
		records() {
			if( this.record_index_active() ) return this.record_refs_all()
			return this.records_all().filter( record => this.$.$mol_error_fence(
				() => !record.deleted(),
				error => error instanceof RangeError && error.message === 'Invalid array length' ? false : error,
			) )
		}

		@ $mol_mem
		records_by_link() {
			return new Map( this.records().map( record => [ record.link().str, record ] ) )
		}

		record_by_link( link: string ) {
			return this.records_by_link().get( link ) ?? null
		}

		@ $mol_mem_key
		record_by_link_direct( link: string ) {
			if( !link ) return null
			return this.$.$mol_error_fence(
				()=> {
					const record = this.$.$giper_baza_glob.Pawn(
						new this.$.$giper_baza_link( link ),
						this.$.$shm_hitalama_map_widget_entity_table2_record,
					)
					record.land().sync()
					const initial = record.Initial()?.val()
					const owner = record.Dataset()?.remote()?.link().str
						?? ( typeof initial?.[ '__dataset' ] === 'string' ? initial[ '__dataset' ] : '' )
					if( owner && new this.$.$giper_baza_link( owner ).str !== this.link().str ) return null
					if( !owner ) return this.record_by_link( record.link().str )
					return record.deleted() ? null : record
				},
				error => this.$.$mol_promise_like( error ) ? error : null,
			)
		}

		broken_record_links() {
			return this.records_all().filter( record => this.$.$mol_error_fence(
				() => { record.deleted(); return false },
				error => error instanceof RangeError && error.message === 'Invalid array length' ? true : error,
			) ).map( record => record.link().str )
		}

		@ $mol_mem
		draft() {
			return this.Draft( null )!.ensure( this.land() )!
		}

		fields_mutable = ()=> true

		field_target_dataset( name: string, next?: $shm_hitalama_map_widget_entity_table2_dataset | null ) {
			const field = this.field( name )
			if( !field ) return null
			return field.target_dataset( next )
		}

		field_relation_value_prepare(
			field: $shm_hitalama_map_widget_entity_table2_field,
			value: any,
			type: 'atom_link' | 'list_link',
		) {
			return this.field_link_value_prepare( value, type )
		}

		schema_snapshot() {
			const fields = this.fields()
			return {
				text: this.text(),
				fields: fields.map( field => field.link().str ),
				values: this.records_all().map( record => ({
					record: record.link().str,
					values: fields.map( field => ({ field: field.link().str, value: record.value( field ) }) ),
				}) ),
			}
		}

		field_schema_write(
			field: $shm_hitalama_map_widget_entity_table2_field,
			data: ReturnType< $shm_hitalama_map_widget_entity_form_custom['field_parse'] >,
		) {
			field.Title( data.name )!.val( data.name )
			field.Type( data.type )!.val( data.type )
			field.Required( data.required )!.val( data.required )
			field.Tags( data.tags )!.items( data.tags )
		}

		@ $mol_action
		schema_restore( snapshot: ReturnType< $shm_hitalama_map_widget_entity_table2_dataset['schema_snapshot'] > ) {
			const fields = snapshot.fields.map( link => this.$.$giper_baza_glob.Pawn(
				new this.$.$giper_baza_link( link ),
				this.$.$shm_hitalama_map_widget_entity_table2_field,
			) )
			const parsed = snapshot.text.split( '\n' ).flatMap( line => line.split( '|' ) )
				.map( line => line.trim() ).filter( Boolean ).map( line => this.field_parse( line ) )
			this.Fields( null )!.remote_list( fields )
			for( let index = 0; index < fields.length; ++ index ) {
				const data = parsed[ index ]
				if( !data ) continue
				this.field_schema_write( fields[ index ], data )
			}
			for( const item of snapshot.values ) {
				const record = this.$.$giper_baza_glob.Pawn(
					new this.$.$giper_baza_link( item.record ),
					this.$.$shm_hitalama_map_widget_entity_table2_record,
				)
				for( const cell of item.values ) {
					const field = fields.find( field => field.link().str === cell.field )
					if( field ) record.value_write_raw( field, cell.value )
				}
			}
			this.draft_reset()
			return this.schema_snapshot()
		}

		field_json_value_prepare( value: any, target_json: boolean ) {
			if( target_json ) {
				if( value === '' || value === null || value === undefined ) return {}
				const parsed = typeof value === 'string' ? JSON.parse( value ) : value
				if( !parsed || Array.isArray( parsed ) || typeof parsed !== 'object' ) {
					throw new Error( 'JSON должен быть объектом' )
				}
				return parsed
			}
			return typeof value === 'object' && value !== null ? JSON.stringify( value ) : String( value ?? '' )
		}

		field_select_options = ( name: string )=> {
			return this.field( name )?.options() ?? []
		}

		@ $mol_action
		field_inverse_pair(
			field: $shm_hitalama_map_widget_entity_table2_field,
			inverse: $shm_hitalama_map_widget_entity_table2_field | null,
		) {
			// Validate once before publishing the pair. A replay after publication
			// must finish its backfill, not treat the partially written pair as done.
			const plan = $mol_wire_sync( this ).field_inverse_pair_plan( field, inverse )
			if( plan.unchanged ) return inverse
			return $shm_hitalama_map_history.transaction( this.map(), 'Изменение обратной связи', ()=> {
				const { target, previous } = plan
				if( previous && target ) {
					field.inverse_field( null )
					previous.inverse_field( null )
					for( const source of this.records() ) {
						const links = source.relation_links( source.value( field ) )
						for( const link of links ) {
							const record = target.record_by_link( link )
							if( !record ) continue
							const current = record.relation_links( record.value( previous ) )
							if( previous.type() === 'list_link' ) record.value_write( previous, current.filter( ( item: string ) => item !== source.link().str ) )
							else if( current.includes( source.link().str ) ) record.value_write( previous, '' )
						}
					}
				}
				if( !inverse ) return null

				inverse.target_dataset( this )
				field.inverse_field( inverse )
				inverse.inverse_field( field )
				for( const source of this.records() ) source.inverse_sync( field, [], source.relation_links( source.value( field ) ) )
				return inverse
			} )
		}

		@ $mol_action
		field_inverse_pair_plan(
			field: $shm_hitalama_map_widget_entity_table2_field,
			inverse: $shm_hitalama_map_widget_entity_table2_field | null,
		) {
			if( !this.fields().some( item => item.link().str === field.link().str ) ) throw new Error( 'Столбец не принадлежит таблице' )
			const target = field.target_dataset()
			const previous = field.inverse_field()
			const unchanged = !!inverse && previous?.link().str === inverse.link().str
			if( inverse ) {
				if( !target?.fields().some( item => item.link().str === inverse.link().str ) ) throw new Error( 'Обратный столбец не принадлежит связанной таблице' )
				if( field.type() !== 'list_link' && inverse.type() !== 'list_link' ) throw new Error( 'Один из связанных столбцов должен быть списком ссылок' )
				if( inverse.type() !== 'atom_link' && inverse.type() !== 'list_link' ) throw new Error( 'Обратный столбец должен содержать ссылки' )
				if( inverse.inverse_field() && inverse.inverse_field()?.link().str !== field.link().str ) throw new Error( 'Обратный столбец уже связан с другим' )
				if( inverse.target_dataset() && inverse.target_dataset()?.link().str !== this.link().str ) throw new Error( 'Обратный столбец ссылается на другую таблицу' )
				if( !unchanged && target.records().some( record => record.relation_links( record.value( inverse ) ).length ) ) {
					throw new Error( 'Для связи выберите пустой обратный столбец' )
				}
			}
			return { target, previous, unchanged }
		}

		@ $mol_action
		field_inverse_create( field: $shm_hitalama_map_widget_entity_table2_field, title: string ) {
			const inverse = $mol_wire_sync( this ).field_inverse_create_prepare( field, title )
			return $mol_wire_sync( this ).field_inverse_pair( field, inverse )
		}

		@ $mol_action
		field_inverse_create_prepare( field: $shm_hitalama_map_widget_entity_table2_field, title: string ) {
			const target = field.target_dataset()
			if( !target ) throw new Error( 'Сначала выберите связанную таблицу' )
			title = title.trim()
			if( !title ) throw new Error( 'Введите название обратного столбца' )
			if( target.field( title ) ) throw new Error( 'Столбец с таким названием уже существует' )
			const inverse = target.Fields( null )!.make( null )
			inverse.title( title )
			inverse.type( 'list_link' )
			target.Fields( null )!.remote_list([ ... target.fields().filter( item => item.link().str !== inverse.link().str ), inverse ])
			return inverse
		}

		record_title( record: $shm_hitalama_map_widget_entity_table2_record, display?: $shm_hitalama_map_widget_entity_table2_field | null ) {
			if( display && display.type() !== 'custom' && this.fields().some( field => field.link().str === display.link().str ) ) {
				const value = record.value( display )
				return value === '' || value === null || value === undefined ? '—' : String( value )
			}
			for( const field of this.fields() ) {
				if( field.type() === 'atom_link' || field.type() === 'list_link' || field.type() === 'custom' ) continue
				const value = record.value( field )
				if( value !== '' && value !== null && value !== undefined ) return String( value )
			}
			return 'Без названия'
		}

		override field_link_options = ( name: string ): Record< string, string > => this.field_link_options_cached( name )

		field_link_label( field: $shm_hitalama_map_widget_entity_table2_field, link: string ) {
			const target = field.target_dataset()
			if( !target || !link ) return link
			const result = this.$.$mol_error_fence(
				()=> {
					const record = target.record_by_link_direct( new this.$.$giper_baza_link( link ).str )
					return record ? target.record_title( record, field.display_field() ) : link
				},
				error => this.$.$mol_promise_like( error ) ? error : link,
			)
			if( this.$.$mol_promise_like( result ) ) throw result
			return result
		}

		@ $mol_mem_key
		field_link_options_cached( name: string ): Record< string, string > {
			const target = this.field_target_dataset( name )
			if( !target ) return {}
			const display = this.field( name )?.display_field()
			return this.$.$mol_error_fence(
				() => Object.fromEntries( target.records().map( record => [
					record.link().toString(),
					this.$.$mol_error_fence(
						() => target.record_title( record, display ),
						error => error instanceof RangeError && error.message === 'Invalid array length'
							? record.link().toString() : error,
					),
				] ) ),
				error => this.$.$mol_promise_like( error ) ? error : {} as Record< string, string >,
			)
		}

		fields_parsed = () => {
			const arr = [ ...this.fields().map( field => ({
				name: field.title(),
				type: field.type(),
				required: field.required(),
				tags: field.tags(),
			}) ), ...$shm_hitalama_map_widget_entity_table2_dataset.system_names.map( name => ({
				name, type: 'string' as const, required: false, tags: [ '#string' ],
			}) ) ]
			return {
				arr,
				dict: Object.fromEntries( arr.map( field => [ field.name, field ] ) ),
			}
		}

		input_field_names = () => {
			return this.fields().filter( field => field.type() !== 'custom' ).map( field => field.title() )
		}

		fields_write = ( fields: readonly ReturnType< $shm_hitalama_map_widget_entity_form_custom['field_parse'] >[] )=> {
			this.text( fields.filter( field => !( $shm_hitalama_map_widget_entity_table2_dataset.system_names as readonly string[] ).includes( field.name ) )
				.map( field => this.field_format( field ) ).join( '\n' ) )
			return true
		}

		text = ( next?: string ): string => {
			if( next === undefined ) return this.fields().map( field => this.field_format({
				name: field.title(), type: field.type(), required: field.required(), tags: field.tags(),
			}) ).join( '\n' )
			return this.text_write( next )
		}

		@ $mol_action
		text_write( next: string ): string {
			const parsed = next.split( '\n' ).flatMap( line => line.split( '|' ) )
				.map( line => line.trim() ).filter( Boolean ).map( line => this.field_parse( line ) )
			if( parsed.some( field => ($shm_hitalama_map_widget_entity_table2_dataset.system_names as readonly string[]).includes( field.name ) ) ) {
				throw new Error( 'Названия системных полей зарезервированы' )
			}
			const names = new Set< string >
			for( const field of parsed ) {
				if( names.has( field.name ) ) throw new Error( `Столбец «${ field.name }» указан несколько раз` )
				names.add( field.name )
			}
			const current = this.fields()
			const by_name = new Map( current.map( field => [ field.title(), field ] ) )
			const used = new Set< string >
			const plans = parsed.map( ( data, index ) => {
				let field = by_name.get( data.name )
				if( field && used.has( field.link().str ) ) field = undefined
				if( !field ) {
					const positional = current[ index ]
					if( positional && !used.has( positional.link().str ) ) field = positional
				}
				if( field ) used.add( field.link().str )
				const previous_type = field?.type()
				const records = field ? [ ... this.records_all(), this.draft() ] : []
				const link_values = field && ( data.type === 'atom_link' || data.type === 'list_link' )
					? records.map( record => ({ record, value: this.field_relation_value_prepare( field!, this.$.$mol_error_fence(
						()=> record.value( field! ),
						error => error instanceof Error && error.message === 'Wrong Link' ? '' : error,
					), data.type as 'atom_link' | 'list_link' ) }) ) : []
				const json_values = field && previous_type !== data.type && ( previous_type === 'json' || data.type === 'json' )
					? records.map( record => ({ record, value: this.field_json_value_prepare( record.value( field! ), data.type === 'json' ) }) ) : []
				return { data, field, link_values, json_values }
			} )
			const before = this.schema_snapshot()
			return $shm_hitalama_map_history.transaction( this.map(), 'Изменение структуры таблицы', ()=> {

				const fields = plans.map( plan => {
					const { data, link_values, json_values } = plan
					let field = plan.field
				field ??= this.Fields( null )!.make( null )
				used.add( field.link().str )
				if( field.inverse_field() && data.type !== 'atom_link' && data.type !== 'list_link' ) this.field_inverse_pair( field, null )
				$shm_hitalama_map_history.silence( ()=> {
					this.field_schema_write( field!, data )
					for( const item of link_values ) item.record.value_write_raw( field!, item.value )
					for( const item of json_values ) item.record.value_write_raw( field!, item.value )
				} )
				return field
			} )
			for( const field of current ) {
				if( !used.has( field.link().str ) && field.inverse_field() ) this.field_inverse_pair( field, null )
			}

				$shm_hitalama_map_history.silence( ()=> this.Fields( null )!.remote_list( fields ) )
				const result = parsed.map( field => this.field_format( field ) ).join( '\n' )
				const before_records = new Map( before.values.map( item => [ item.record, item ] ) )
				const after = {
					text: result,
					fields: fields.map( field => field.link().str ),
					values: before.values.map( item => ({
						record: item.record,
						values: fields.map( ( field, index ) => {
							const plan = plans[ index ]
							const migrated = [ ...plan.link_values, ...plan.json_values ]
								.find( value => value.record.link().str === item.record )
							const previous = before_records.get( item.record )?.values
								.find( value => value.field === field.link().str )?.value
							return { field: field.link().str, value: migrated?.value ?? previous ?? '' }
						} ),
					}) ),
				}
				$shm_hitalama_map_history.capture( this.map(), {
					type: 'dataset_schema',
					target: this.link().str,
					targets: [ ... new Set([ ... current, ... fields ].map( field => field.link().str )) ],
					before,
					after,
				}, 'Изменение структуры таблицы' )
				return result
			} )
		}

		object_current = ( next?: Record< string, any > ): Record< string, any > => {
			const draft = this.draft()
			if( next !== undefined ) draft.object_write( this.fields(), next )
			return draft.object( this.fields() )
		}

		object_current_value = ( name: string, next?: any )=> {
			const field = this.fields().find( field => field.title() === name )
			if( !field ) return ''
			return this.draft().value( field, next )
		}

		objects = ( next?: readonly any[] ): readonly any[] => {
			if( next === undefined ) return this.records().map( record => ({ ...record.object( this.fields() ), ...record.system_values() }) )
			return $shm_hitalama_map_history.transaction( this.map(), 'Массовое изменение таблицы', ()=> {
				const records = $mol_wire_sync( this ).records()
				const existing = Math.min( records.length, next.length )
				if( existing ) $mol_wire_sync( this ).records_write(
					$mol_wire_sync( records ).slice( 0, existing ), $mol_wire_sync( next ).slice( 0, existing ),
				)
				if( next.length > existing ) $mol_wire_sync( this ).records_add( $mol_wire_sync( next ).slice( existing ) )
				if( next.length < records.length ) $mol_wire_sync( this ).records_delete_links( $mol_wire_sync( records ).slice( next.length ) )
				return next
			} )
		}

		private batch_value_prepare( field: $shm_hitalama_map_widget_entity_table2_field, next: any ) {
			if( next === '' || next === null || next === undefined ) {
				if( field.type() === 'list_link' ) return []
				if( field.type() === 'json' ) return {}
				return ''
			}
			switch( field.type() ) {
				case 'number': {
					const number = Number( next )
					if( !Number.isFinite( number ) ) throw new Error( 'Число должно быть конечным' )
					return number
				}
				case 'json': {
					const value = typeof next === 'string' ? JSON.parse( next ) : next
					if( !value || Array.isArray( value ) || typeof value !== 'object' ) throw new Error( 'JSON должен быть объектом' )
					return value
				}
				case 'atom_link':
				case 'file': return String( next )
				case 'list_link': return ( Array.isArray( next ) ? next : [ next ] ).map( String )
				default: return String( next )
			}
		}

		private readonly records_write_plans = new Map< string, {
			records: readonly $shm_hitalama_map_widget_entity_table2_record[]
			stamp: number
			user: string
			cells: readonly {
				record: $shm_hitalama_map_widget_entity_table2_record
				field: $shm_hitalama_map_widget_entity_table2_field
				before: any
				after: any
			}[]
		} >

		@ $mol_action
		records_write_plan(
			records: readonly $shm_hitalama_map_widget_entity_table2_record[],
			objects: readonly Record< string, any >[],
		) {
			const count = Math.min( records.length, objects.length )
			const selected = records.slice( 0, count )
			const key = `${ selected.map( record => record.link().str ).join( '|' ) }\n${ JSON.stringify( objects.slice( 0, count ) ) }`
			let plan = this.records_write_plans.get( key )
			if( !plan ) {
				const fields = this.fields()
				const cells = new Map< string, {
					record: $shm_hitalama_map_widget_entity_table2_record
					field: $shm_hitalama_map_widget_entity_table2_field
					before: any
					after: any
				} >
				const direct_relations: Array< {
					record: $shm_hitalama_map_widget_entity_table2_record
					field: $shm_hitalama_map_widget_entity_table2_field
					before: any
					after: any
				} > = []
				const cell_plan = (
					record: $shm_hitalama_map_widget_entity_table2_record,
					field: $shm_hitalama_map_widget_entity_table2_field,
					after: any,
				) => {
					const key = `${ record.link().str }:${ field.link().str }`
					const current = cells.get( key )
					const before = this.batch_value_prepare( field, current?.before ?? record.value( field ) )
					const prepared = this.batch_value_prepare( field, after )
					if( $shm_hitalama_map_history.equal( before, prepared ) ) cells.delete( key )
					else cells.set( key, { record, field, before, after: prepared } )
				}

				for( let index = 0; index < count; ++ index ) {
					const record = selected[ index ]
					const object = objects[ index ]
					for( const field of fields ) {
						if( !Object.prototype.hasOwnProperty.call( object, field.title() ) ) continue
						const before = record.value( field )
						const after = this.batch_value_prepare( field, object[ field.title() ] )
						if( $shm_hitalama_map_history.equal( before, after ) ) continue
						cell_plan( record, field, after )
						if( field.type() === 'atom_link' || field.type() === 'list_link' ) {
							direct_relations.push({ record, field, before, after })
						}
					}
				}

				for( const change of direct_relations ) {
					const inverse = change.field.inverse_field()
					const target = change.field.target_dataset()
					if( !inverse || !target || inverse.inverse_field()?.link().str !== change.field.link().str ) continue
					if( !target.fields().some( field => field.link().str === inverse.link().str ) ) continue
					const before = new Set( change.record.relation_links( change.before ) )
					const after = new Set( change.record.relation_links( change.after ) )
					for( const link of new Set([ ... before, ... after ]) ) {
						const was = before.has( link )
						const is = after.has( link )
						if( was === is ) continue
						const record = target.record_by_link( link )
						if( !record ) continue
						const planned = cells.get( `${ record.link().str }:${ inverse.link().str }` )
						const current = planned?.after ?? record.value( inverse )
						if( inverse.type() === 'list_link' ) {
							const links = record.relation_links( current )
							cell_plan( record, inverse, is
								? [ ... new Set([ ... links, change.record.link().str ]) ]
								: links.filter( item => item !== change.record.link().str ) )
						} else if( inverse.type() === 'atom_link' ) {
							if( is ) cell_plan( record, inverse, change.record.link().str )
							else if( record.relation_links( current ).includes( change.record.link().str ) ) cell_plan( record, inverse, '' )
						}
					}
				}

				plan = { records: selected, cells: [ ... cells.values() ],
					stamp: $mol_wire_sync( Date ).now(), user: this.$.$shm_hitalama_profile.current()?.link().str ?? '',
				}
				this.records_write_plans.set( key, plan )
			}

			return { key, plan }
		}

		@ $mol_action
		records_write(
			records: readonly $shm_hitalama_map_widget_entity_table2_record[],
			objects: readonly Record< string, any >[],
		) {
			return $shm_hitalama_map_history.transaction( this.map(), 'Массовое изменение строк', ()=> {
				const { key, plan } = $mol_wire_sync( this ).records_write_plan( records, objects )
				if( plan.cells.length ) {
					$shm_hitalama_map_history.capture( this.map(), {
						type: 'rows_update', target: this.link().str,
						targets: [ ... new Set([
							this.link().str,
							... plan.cells.flatMap( cell => [ cell.record.link().str, cell.field.link().str ] ),
						]) ],
						before: plan.cells.map( cell => $shm_hitalama_map_history.value( cell.before ) ),
						after: plan.cells.map( cell => $shm_hitalama_map_history.value( cell.after ) ),
						meta: { cells: plan.cells.map( cell => ({ record: cell.record.link().str, field: cell.field.link().str }) ) },
					}, 'Массовое изменение строк' )
					$shm_hitalama_map_history.silence( ()=> {
						const touched = new Set< $shm_hitalama_map_widget_entity_table2_record >
						for( const cell of plan.cells ) {
							$mol_wire_sync( cell.record ).value_write_raw( cell.field, cell.after )
							touched.add( cell.record )
						}
						for( const record of touched ) $mol_wire_sync( record ).metadata_touch( plan.stamp, plan.user )
					} )
				}
				this.records_write_plans.delete( key )
				return plan.records
			} )
		}

		@ $mol_action
		record_add( object: Record< string, any > ) {
			const objects = $mol_wire_sync( Array ).of( object ) as Record<string, any>[]
			const records = $mol_wire_sync( this ).records_add_transaction( objects, 'Добавление строки', object )
			$mol_wire_sync( this ).refresh_query_snapshots( records )
			return records[ 0 ]
		}

		@ $mol_action
		records_add( objects: readonly Record< string, any >[] ) {
			const records = $mol_wire_sync( this ).records_add_transaction( objects, 'Добавление строк', objects )
			$mol_wire_sync( this ).refresh_query_snapshots( records )
			return records
		}

		@ $mol_action
		prepare_query_snapshot_groups( records: readonly $shm_hitalama_map_widget_entity_table2_record[] ) {
			const groups = new Map<$giper_baza_land, $shm_hitalama_map_widget_entity_table2_record[]>()
			// Several rows share a writer. Persist each physical writer once, including
			// detached writers of the same link, before resolving any canonical copy.
			for( const land of new Set( records.map( record=> record.land() ) ) ) land.units_saving()
			const lands = new Map<string, $giper_baza_land>()
			for( const record of records ) {
				// Command results can retain a detached Land after history suspended.
				// Finish its source writes before resolving the canonical local copy:
				// otherwise a manifest can describe a partially delivered source.
				const link = record.link().land()
				let land = lands.get( link.str )
				if( !land ) {
					land = this.$.$giper_baza_glob.Land( link )
					land.sync()
					lands.set( link.str, land )
				}
				let rows = groups.get( land )
				if( !rows ) groups.set( land, rows = [] )
				rows.push( land.Pawn( this.$.$shm_hitalama_map_widget_entity_table2_record ).Head( record.head() ) )
			}
			return groups
		}

		@ $mol_action
		refresh_query_snapshots( records: readonly $shm_hitalama_map_widget_entity_table2_record[] ) {
			// Stabilize group arrays across suspension; no effects in a getter/render.
			const groups = $mol_wire_sync( this ).prepare_query_snapshot_groups( records )
			for( const [land, rows] of groups ) $mol_wire_sync( this.query_snapshot( land ) ).refresh( this, rows )
		}

		private readonly records_add_plans = new WeakMap< object, {
			records: $shm_hitalama_map_widget_entity_table2_record[]
			offset: number
			metadata: { dataset: $shm_hitalama_map_widget_entity_table2_dataset, stamp: number, user: string }
			values: Map< string, Map< string, any > >
		} >

		@ $mol_action
		records_add_plan_create( key: object ) {
			let plan = this.records_add_plans.get( key )
			if( plan ) return plan
			this.record_index_migrate()
			if( !this.record_index_active() ) this.record_index_activate()
			plan = { records: [], offset: this.record_sequence(),
				metadata: { dataset: this, stamp: Date.now(), user: this.$.$shm_hitalama_profile.current()?.link().str ?? '' },
				values: new Map }
			this.records_add_plans.set( key, plan )
			return plan
		}

		@ $mol_action
		records_add_reserve_one( plan: ReturnType< $shm_hitalama_map_widget_entity_table2_dataset['records_add_plan_create'] >, index: number ) {
			if( plan.records[index] ) return plan.records[index]
			const shard = Math.floor( ( plan.offset + index ) / $shm_hitalama_map_widget_entity_table2_dataset.record_shard_size )
			const area = this.record_shard( shard )
			const record = area.Pawn( $shm_hitalama_map_widget_entity_table2_record ).Head( area.self_make() )
			plan.records[index] = record
			return record
		}

		@ $mol_action
		records_add_prepare( key: object, count: number ) {
			const plan = $mol_wire_sync( this ).records_add_plan_create( key )
			// Keep every reservation call on replay, including completed earlier shards.
			for( let index = 0; index < count; ++index ) $mol_wire_sync( this ).records_add_reserve_one( plan, index )
			const until = plan.offset + count
			if( this.record_sequence() < until ) this.Record_sequence( null )!.val( until )
			return plan
		}

		@ $mol_action
		records_add_initialize_one(
			record: $shm_hitalama_map_widget_entity_table2_record,
			object: Record< string, any >,
			fields: readonly $shm_hitalama_map_widget_entity_table2_field[],
			metadata: { dataset: $shm_hitalama_map_widget_entity_table2_dataset, stamp: number, user: string },
		) {
			const values = new Map< string, any >
			for( const field of fields ) {
				if( !Object.prototype.hasOwnProperty.call( object, field.title() ) ) continue
				const value = object[field.title()]
				if( value === '' || value === null ) continue
				values.set( field.link().str, record.value_prepare( field, value ) )
			}
			$mol_wire_sync( record ).initial_write( fields, object, metadata )
			return values
		}

		@ $mol_action
		records_add_initialize(
			records: readonly $shm_hitalama_map_widget_entity_table2_record[],
			objects: readonly Record< string, any >[],
			fields: readonly $shm_hitalama_map_widget_entity_table2_field[],
			metadata: { dataset: $shm_hitalama_map_widget_entity_table2_dataset, stamp: number, user: string },
			written: Map< string, Map< string, any > >,
			from: number,
			until: number,
		) {
			for( let index = from; index < until; ++index ) {
				const record = records[index], object = objects[index]
				const values = $mol_wire_sync( this ).records_add_initialize_one( record, object, fields, metadata )
				written.set( record.link().str, values )
			}
			return true
		}

		@ $mol_action
		records_add_transaction( objects: readonly Record< string, any >[], title: string, key: object ) {
			return $shm_hitalama_map_history.transaction( this.map(), title, ()=> {
				const fields = this.fields()
				const relation_fields = fields.filter( field =>
					field.type() === 'atom_link' || field.type() === 'list_link'
				)
				const target_indexes = new Map< string, Map< string, $shm_hitalama_map_widget_entity_table2_record > >
				const inverse_lists = new Map< string, {
					record: $shm_hitalama_map_widget_entity_table2_record
					field: $shm_hitalama_map_widget_entity_table2_field
					sources: string[]
				} >
				const inverse_atoms: Array< {
					source: $shm_hitalama_map_widget_entity_table2_record
					field: $shm_hitalama_map_widget_entity_table2_field
					target: $shm_hitalama_map_widget_entity_table2_dataset
					links: readonly string[]
				} > = []

				const add_plan = $mol_wire_sync( this ).records_add_prepare( key, objects.length )
				const records = add_plan.records
				const written_values = add_plan.values
				for( let from = 0; from < records.length; from += 32 ) {
					$mol_wire_sync( this ).records_add_initialize( records, objects, fields, add_plan.metadata, written_values, from, Math.min( records.length, from + 32 ) )
					if( from + 32 < records.length ) this.$.$mol_wait_rest()
				}
				if( records.length ) {
					this.record_index_append( records )
					this.Storage_version( null )!.val( $shm_hitalama_map_widget_entity_table2_dataset.storage_version )
				}

				for( const source of records ) for( const field of relation_fields ) {
					const inverse = field.inverse_field()
					const target = field.target_dataset()
					if( !inverse || !target || inverse.inverse_field()?.link().str !== field.link().str ) continue
					if( !target.fields().some( item => item.link().str === inverse.link().str ) ) continue
					const links = source.relation_links( written_values.get( source.link().str )?.get( field.link().str ) )
					if( !links.length ) continue
					if( inverse.type() === 'atom_link' ) {
						inverse_atoms.push({ source, field, target, links })
						continue
					}
					if( inverse.type() !== 'list_link' ) continue
					let index = target_indexes.get( target.link().str )
					if( !index ) {
						const indexed = target.records_all()
						const local = target.link().str === this.link().str ? records : []
						target_indexes.set( target.link().str, index = new Map(
							[ ... indexed, ... local ].map( record => [ record.link().str, record ] ),
						) )
					}
					for( const link of links ) {
						const record = index.get( link )
						if( !record || record.deleted() ) continue
						const key = `${ record.link().str }:${ inverse.link().str }`
						let update = inverse_lists.get( key )
						if( !update ) inverse_lists.set( key, update = { record, field: inverse, sources: [] })
						update.sources.push( source.link().str )
					}
				}

				// Inverse writes are a derived part of the rows_add operation. Recording
				// every target cell as a separate history change duplicates the growing
				// relation arrays and retains thousands of reactive history targets during
				// large imports. The rows_add event already restores direct values, whose
				// normal inverse synchronization restores these derived values on redo.
				for( const update of inverse_lists.values() ) {
					const owner = update.record.dataset()
					if( !owner ) continue
					const values = written_values.get( update.record.link().str )
					const raw = values?.has( update.field.link().str )
						? values.get( update.field.link().str )
						: update.record.value( update.field )
					if( !owner.relation_has_pages( update.record, update.field ) ) {
						owner.relation_replace( update.record, update.field, update.record.relation_links( raw ) )
					}
					const current = new Set( owner.relation_values( update.record, update.field ) )
					owner.relation_append( update.record, update.field, update.sources.filter( source => !current.has( source ) ) )
					if( !values ) update.record.metadata_touch( add_plan.metadata.stamp, add_plan.metadata.user )
				}
				$shm_hitalama_map_history.silence( ()=> {
					// A scalar inverse has replacement semantics. Write it sequentially so
					// moving a target still clears its previous source. Use the batch-local
					// index because reactive record snapshots may not include newly appended
					// records until the next wire turn.
					for( const update of inverse_atoms ) {
						let index = target_indexes.get( update.target.link().str )
						if( !index ) {
							const local = update.target.link().str === this.link().str ? records : []
							target_indexes.set( update.target.link().str, index = new Map(
								[ ... update.target.records_all(), ... local ].map( record => [ record.link().str, record ] ),
							) )
						}
						const inverse = update.field.inverse_field()!
						for( const link of update.links ) {
							const record = index.get( link )
							if( !record || record.deleted() ) continue
							record.value_write( inverse, update.source.link().str, false, true )
						}
					}
				} )

				if( records.length === 1 ) {
					const record = records[ 0 ]
					$shm_hitalama_map_history.capture( this.map(), {
						type: 'row_add',
						target: record.link().str,
						targets: [
							this.link().str,
							... fields.map( field => field.link().str ),
							... this.history_scope_targets( records ),
						],
						before: true,
						after: false,
						meta: { dataset: this.link().str, values: fields.map( field => ({
							field: field.link().str,
							value: $shm_hitalama_map_history.value( written_values.get( record.link().str )?.get( field.link().str ) ?? '' ),
						}) ) },
					}, title )
				} else if( records.length ) {
					const field_links = fields.map( field => field.link().str )
					$shm_hitalama_map_history.capture( this.map(), {
						type: 'rows_add',
						target: this.link().str,
						targets: [
							... records.map( record => record.link().str ),
							... fields.map( field => field.link().str ),
							... this.history_scope_targets( records ),
						],
						before: records.map( ()=> true ),
						after: records.map( ()=> false ),
						meta: {
							dataset: this.link().str,
							// Store the schema once for the whole batch. Repeating field links
							// in every row amplified history allocations during large imports.
							fields: field_links,
							rows: records.map( record => ({
								record: record.link().str,
								values: fields.map( field => $shm_hitalama_map_history.value(
									written_values.get( record.link().str )?.get( field.link().str ) ?? '',
								) ),
							}) ),
						},
					}, title )
				}

				// Record shards are separate lands. Merely storing their links in Records
				// does not keep their persistence and network fibers alive, so a fresh
				// client could see the rows but not their Initial values. Root every
				// touched shard after all row and inverse writes are complete.
				for( const land of new Map( records.map( record => [ record.land().link().str, record.land() ] ) ).values() ) {
					land.sync()
				}

				this.records_add_plans.delete( key )
				return records
			} )
		}

		@ $mol_action
		record_update( index: number, object: Record< string, any > ) {
			const record = this.records()[ index ]
			if( !record ) return null
			record.object_write( this.fields(), object )
			return record
		}

		@ $mol_action
		record_delete( index: number ) {
			const record = this.records()[ index ]
			if( !record ) return false
			this.record_delete_links( record )
			return true
		}

		@ $mol_action
		record_delete_links( record: $shm_hitalama_map_widget_entity_table2_record ) {
			const records = $mol_wire_sync( Array ).of( record ) as $shm_hitalama_map_widget_entity_table2_record[]
			return $mol_wire_sync( this ).records_delete_links( records )[ 0 ]
		}

		private readonly records_delete_plans = new Map< string, {
			captured?: boolean
			active: readonly $shm_hitalama_map_widget_entity_table2_record[]
			fields: readonly $shm_hitalama_map_widget_entity_table2_field[]
			snapshots: Map< string, ReturnType< $shm_hitalama_map_widget_entity_table2_dataset['record_snapshot'] > >
			references: readonly {
				record: $shm_hitalama_map_widget_entity_table2_record
				field: $shm_hitalama_map_widget_entity_table2_field
				before: any
				after: any
			}[]
			reference_meta: readonly { record: string, field: string, before: any, after: any }[]
			targets: readonly string[]
		} >

		@ $mol_action
		records_delete_links( records: readonly $shm_hitalama_map_widget_entity_table2_record[] ) {
			const task = $mol_wire_sync( this ).records_delete_task( records )
			const result = $mol_wire_sync( $shm_hitalama_map_history ).transaction(
				this.map(), records.length > 1 ? 'Удаление строк' : 'Удаление строки', task,
			) as readonly $shm_hitalama_map_widget_entity_table2_record[]
			// Keep the replay plan until the history commit is complete, not merely
			// until row writes finish. The commit can suspend too.
			this.records_delete_plans.delete( [ ...new Set( records.map( record=> record.link().str ) ) ].sort().join( '|' ) )
			return result
		}

		records_delete_task( records: readonly $shm_hitalama_map_widget_entity_table2_record[] ) {
			return ()=> {
				const key = [ ... new Set( records.map( record => record.link().str ) ) ].sort().join( '|' )
				let plan = this.records_delete_plans.get( key )
				if( !plan ) {
					const active = records.filter( record => !record.deleted() )
					if( !active.length ) return []
					const links = new Set( active.map( record => record.link().str ) )
					const snapshots = new Map( active.map( record => [ record.link().str, this.record_snapshot( record ) ] ) )
					const fields = this.fields()
					const reference_plans = new Map< string, {
						record: $shm_hitalama_map_widget_entity_table2_record
						field: $shm_hitalama_map_widget_entity_table2_field
						before: any
						after: any
					} >
					const reference_plan = (
						record: $shm_hitalama_map_widget_entity_table2_record,
						field: $shm_hitalama_map_widget_entity_table2_field,
						after: any,
					) => {
						const key = `${ record.link().str }:${ field.link().str }`
						const current = reference_plans.get( key )
						if( current ) current.after = after
						else reference_plans.set( key, { record, field, before: record.value( field ), after } )
					}

					for( const dataset of this.map()?.Datasets()?.remote_list() ?? [] ) {
						for( const field of dataset.fields() ) {
							if( field.target_dataset()?.link().str !== this.link().str ) continue
							for( const source of dataset.records() ) {
								const before = source.value( field )
								const current = source.relation_links( before )
								const next = current.filter( link => !links.has( link ) )
								if( next.length === current.length ) continue
								reference_plan( source, field, field.type() === 'list_link' ? next : '' )
							}
						}
					}

					for( const source of active ) for( const field of fields ) {
						if( field.type() !== 'atom_link' && field.type() !== 'list_link' ) continue
						const inverse = field.inverse_field()
						const target = field.target_dataset()
						if( !inverse || !target || inverse.inverse_field()?.link().str !== field.link().str ) continue
						if( !target.fields().some( item => item.link().str === inverse.link().str ) ) continue
						for( const link of source.relation_links( source.value( field ) ) ) {
							const record = target.record_by_link( link )
							if( !record ) continue
							const planned = reference_plans.get( `${ record.link().str }:${ inverse.link().str }` )
							const before = planned?.after ?? record.value( inverse )
							const current = record.relation_links( before )
							const next = inverse.type() === 'list_link'
								? current.filter( item => item !== source.link().str )
								: current.includes( source.link().str ) ? '' : before
							if( JSON.stringify( before ) !== JSON.stringify( next ) ) reference_plan( record, inverse, next )
						}
					}

					const references = [ ... reference_plans.values() ]
					const reference_meta = references.map( reference => ({
						record: reference.record.link().str,
						field: reference.field.link().str,
						before: $shm_hitalama_map_history.value( reference.before ),
						after: $shm_hitalama_map_history.value( reference.after ),
					}) )
					const targets = [ ... new Set([
						this.link().str,
						... active.map( record => record.link().str ),
						... fields.map( field => field.link().str ),
						... reference_meta.flatMap( reference => [ reference.record, reference.field ] ),
						... this.history_scope_targets( active ),
					]) ]
					plan = { active, fields, snapshots, references, reference_meta, targets }
					this.records_delete_plans.set( key, plan )
				}

				const { active, fields, snapshots, references, reference_meta, targets } = plan

				// Later writes can suspend and restart this transaction body. Keep both
				// the event payload and the capture task stable instead of appending the
				// same removal to the history context on each replay.
				if( !plan.captured ) {
					const change = $mol_wire_sync( this ).records_delete_change( active, snapshots, reference_meta, targets )
					$mol_wire_sync( $shm_hitalama_map_history ).capture(
						this.map(), change, active.length === 1 ? 'Удаление строки' : 'Удаление строк',
					)
					plan.captured = true
				}

				const stamp = $mol_wire_sync( Date ).now()
				const user = this.$.$shm_hitalama_profile.current()?.link().str ?? ''
				$shm_hitalama_map_history.silence( ()=> {
					for( const reference of references ) {
						$mol_wire_sync( reference.record ).value_write_raw( reference.field, reference.after )
						$mol_wire_sync( reference.record ).metadata_touch( stamp, user )
					}
					for( const record of active ) {
						for( const field of fields ) {
							if( field.type() === 'atom_link' ) $mol_wire_sync( record ).value_write_raw( field, '' )
							if( field.type() === 'list_link' ) $mol_wire_sync( record ).value_write_raw( field, [] )
						}
						$mol_wire_sync( record ).deleted( true )
						$mol_wire_sync( record ).metadata_touch( stamp, user )
					}
					$mol_wire_sync( this ).record_index_remove( active )
				} )

				return active
			}
		}

		records_delete_change(
			active: readonly $shm_hitalama_map_widget_entity_table2_record[],
			snapshots: ReadonlyMap< string, ReturnType< $shm_hitalama_map_widget_entity_table2_dataset['record_snapshot'] > >,
			references: readonly { record: string, field: string, before: any, after: any }[],
			targets: readonly string[],
		): $shm_hitalama_map_history_change {
			if( active.length === 1 ) return {
				type: 'row_remove', target: active[ 0 ].link().str, targets: [ ...targets ],
				before: false, after: true,
				meta: { dataset: this.link().str, values: snapshots.get( active[ 0 ].link().str ), references },
			}
			return {
				type: 'rows_remove', target: this.link().str, targets: [ ...targets ],
				before: active.map( ()=> false ), after: active.map( ()=> true ),
				meta: {
					dataset: this.link().str, references,
					rows: active.map( record=> ({ record: record.link().str, values: snapshots.get( record.link().str ) }) ),
				},
			}
		}

		@ $mol_action
		draft_reset() {
			// A normal pawn read lets giper_baza load this area and its inherited rights.
			this.records_all()
			const draft = this.land().Pawn( $shm_hitalama_map_widget_entity_table2_record ).Head( this.land().self_make() )
			this.Draft( null )!.remote( draft )
			return draft
		}

		@ $mol_action
		draft_relation_prepare(
			field: $shm_hitalama_map_widget_entity_table2_field,
			source: $shm_hitalama_map_widget_entity_table2_record,
		) {
			const draft = this.draft_reset()
			const inverse = field.inverse_field()
			if( !inverse ) return draft
			if( field.target_dataset()?.link().str !== this.link().str ) return draft
			if( inverse.inverse_field()?.link().str !== field.link().str ) return draft
			if( !this.fields().some( item => item.link().str === inverse.link().str ) ) return draft

			const link = source.link().str
			draft.value_write_raw( inverse, inverse.type() === 'list_link' ? [ link ] : link )
			return draft
		}

	}

}
